import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  CheckCircle2,
  Shield,
  Monitor,
  GraduationCap,
  PartyPopper,
  Scissors,
  Palette,
  Brush
} from 'lucide-react';

/**
 * Creative Hobbies Service Cards
 * Includes Art & Craft, Kids' Creative, DIY, Sewing, Party Hosting, and Pottery services
 */

// Card A: Anchor Card - Art & Craft Classes
export const ArtCraftClassCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  hasOnlineOption = true,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-rose-200 bg-rose-50/20'
      }`}
      data-testid="art-craft-class-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Creative Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🎨</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Art &amp; Craft Classes</h3>
            <p className="text-xs text-gray-500">Painting · Drawing · Crafts</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-rose-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Beginner-friendly</span>
          </p>
          <p className="text-xs text-rose-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Online or local</span>
          </p>
          <p className="text-xs text-green-700 flex items-center gap-1">
            <GraduationCap className="h-3 w-3 flex-shrink-0" />
            <span>Verified instructor</span>
          </p>
        </div>

        {/* Rating & Location */}
        <div className="flex items-center justify-between text-xs mb-2">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
            <span className="text-gray-500">({provider?.review_count || 310} classes)</span>
          </div>
          <span className="text-gray-500 flex items-center gap-1">
            {hasOnlineOption && <Monitor className="h-3 w-3" />}
            <span>{hasOnlineOption ? 'Online' : ''}{hasOnlineOption && provider?.travel_radius_km ? ' / ' : ''}{provider?.travel_radius_km ? `${provider.travel_radius_km}km` : ''}</span>
          </span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 12}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-rose-600 hover:bg-rose-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-art-class"
        >
          Book class
        </Button>
      </CardContent>
    </Card>
  );
};

// Card B: Kids' Creative Classes (Trust-First)
export const KidsCreativeClassCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  isBackgroundChecked = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border-2 hover:shadow-md transition-all ${
        isBackgroundChecked 
          ? 'border-green-300 bg-green-50/20' 
          : 'border-gray-200 bg-gray-50'
      }`}
      data-testid="kids-creative-class-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Kids' Creative</span>
          </div>
        )}

        {/* Trust Badge - ALWAYS FIRST for kids' services */}
        <div className={`flex items-center gap-1 text-xs rounded px-2 py-1 mb-2 ${
          isBackgroundChecked ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-600'
        }`}>
          <Shield className="h-3 w-3" />
          <span>{isBackgroundChecked ? 'Background checked' : 'Verification pending'}</span>
        </div>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧒</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Kids' Art &amp; Craft Classes</h3>
            <p className="text-xs text-gray-500">Drawing · Painting · Fun crafts</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className={`text-xs flex items-center gap-1 ${isBackgroundChecked ? 'text-green-700' : 'text-gray-400'}`}>
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Age-appropriate activities</span>
          </p>
        </div>

        {/* Rating (only show if verified) */}
        {isBackgroundChecked && (
          <div className="flex items-center gap-1 text-xs mb-2">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
            <span className="text-gray-500">({provider?.review_count || 180} parents)</span>
            {provider?.travel_radius_km && (
              <span className="text-gray-500 ml-1">· {provider.travel_radius_km}km</span>
            )}
          </div>
        )}

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 10}</span>
        </p>

        {/* CTA */}
        <Button 
          className={`w-full h-8 text-sm ${isBackgroundChecked ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400'}`}
          onClick={() => isBackgroundChecked && onBook?.(service, provider)}
          disabled={!isBackgroundChecked}
          data-testid="book-kids-creative"
        >
          {isBackgroundChecked ? 'Book kids class' : 'Requires verification'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Card C: DIY Project Assistance
export const DIYAssistanceCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-amber-200 bg-amber-50/20'
      }`}
      data-testid="diy-assistance-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · DIY Help</span>
          </div>
        )}

        {/* Practical Badge */}
        <Badge className="bg-amber-100 text-amber-700 text-xs mb-2">
          High Conversion
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧰</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">DIY Project Assistance</h3>
            <p className="text-xs text-gray-500">Kits · Crafts · Step-by-step</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Finish what you started</span>
          </p>
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Home or online help</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.7'}</span>
          <span className="text-gray-500">({provider?.review_count || 145} projects)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 20}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-amber-600 hover:bg-amber-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-diy-help"
        >
          Get help
        </Button>
      </CardContent>
    </Card>
  );
};

// Card D: Sewing & Knitting Workshops
export const SewingWorkshopCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-purple-200 bg-purple-50/20'
      }`}
      data-testid="sewing-workshop-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Textile Workshop</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧵</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Sewing &amp; Knitting Workshops</h3>
            <p className="text-xs text-gray-500">Sewing · Crochet · Embroidery</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Tools &amp; materials guidance</span>
          </p>
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Beginner to intermediate</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
          <span className="text-gray-500">({provider?.review_count || 120} sessions)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 18}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-purple-600 hover:bg-purple-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="join-sewing-workshop"
        >
          Join workshop
        </Button>
      </CardContent>
    </Card>
  );
};

// Card E: Creative Party / Event Hosting
export const CreativePartyCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  isVerifiedHost = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-pink-200 bg-pink-50/20'
      }`}
      data-testid="creative-party-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Creative Event</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🎉</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Creative Craft Party Host</h3>
            <p className="text-xs text-gray-500">Kids · Adults · Groups</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-pink-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Guided creative activities</span>
          </p>
          {isVerifiedHost && (
            <p className="text-xs text-green-700 flex items-center gap-1">
              <Shield className="h-3 w-3 flex-shrink-0" />
              <span>Verified host</span>
            </p>
          )}
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
          <span className="text-gray-500">({provider?.review_count || 95} events)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 60}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-pink-600 hover:bg-pink-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-creative-party"
        >
          Book event
        </Button>
      </CardContent>
    </Card>
  );
};

// ==================== POTTERY SERVICES ====================

// Pottery A: Beginner Pottery Classes (Anchor)
export const BeginnerPotteryCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-orange-200 bg-orange-50/20'
      }`}
      data-testid="beginner-pottery-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Creative Workshop</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🏺</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Beginner Pottery Classes</h3>
            <p className="text-xs text-gray-500">Hand-building · Wheel basics</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-orange-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>No experience needed</span>
          </p>
          <p className="text-xs text-orange-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Materials included</span>
          </p>
          <p className="text-xs text-green-700 flex items-center gap-1">
            <GraduationCap className="h-3 w-3 flex-shrink-0" />
            <span>Studio verified</span>
          </p>
        </div>

        {/* Rating & Location */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
          <span className="text-gray-500">({provider?.review_count || 210} classes)</span>
          {provider?.travel_radius_km && (
            <span className="text-gray-500">· {provider.travel_radius_km}km</span>
          )}
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 25}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-orange-600 hover:bg-orange-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="join-pottery-class"
        >
          Join class
        </Button>
      </CardContent>
    </Card>
  );
};

// Pottery B: Wheel Throwing Class (Premium)
export const WheelThrowingCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-teal-200 bg-teal-50/20'
      }`}
      data-testid="wheel-throwing-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Pottery Studio</span>
          </div>
        )}

        {/* Premium Badge */}
        <Badge className="bg-teal-100 text-teal-700 text-xs mb-2">
          Premium Experience
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🌀</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Wheel Throwing Pottery</h3>
            <p className="text-xs text-gray-500">Cups · Bowls · Forms</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-teal-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Hands-on wheel training</span>
          </p>
          <p className="text-xs text-teal-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Small group sessions</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
          <span className="text-gray-500">({provider?.review_count || 140} sessions)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 40}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-teal-600 hover:bg-teal-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-wheel-class"
        >
          Book wheel class
        </Button>
      </CardContent>
    </Card>
  );
};

// Pottery C: Glazing & Firing Service
export const GlazingFiringCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-indigo-200 bg-indigo-50/20'
      }`}
      data-testid="glazing-firing-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Pottery Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🎨</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Glazing &amp; Firing</h3>
            <p className="text-xs text-gray-500">Finish &amp; fire your pieces</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-indigo-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Kiln-fired</span>
          </p>
          <p className="text-xs text-indigo-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Food-safe glazes (where applicable)</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
          <span className="text-gray-500">({provider?.review_count || 320} firings)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 15}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-indigo-600 hover:bg-indigo-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-firing"
        >
          Book firing
        </Button>
      </CardContent>
    </Card>
  );
};

// Pottery D: Kids' Pottery Classes (Trust-First)
export const KidsPotteryCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  isBackgroundChecked = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border-2 hover:shadow-md transition-all ${
        isBackgroundChecked 
          ? 'border-green-300 bg-green-50/20' 
          : 'border-gray-200 bg-gray-50'
      }`}
      data-testid="kids-pottery-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Kids' Creative</span>
          </div>
        )}

        {/* Trust Badge - ALWAYS FIRST */}
        <div className={`flex items-center gap-1 text-xs rounded px-2 py-1 mb-2 ${
          isBackgroundChecked ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-600'
        }`}>
          <Shield className="h-3 w-3" />
          <span>{isBackgroundChecked ? 'Background checked' : 'Verification pending'}</span>
        </div>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧒</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Kids' Pottery Classes</h3>
            <p className="text-xs text-gray-500">Fun · Safe · Mess-friendly</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className={`text-xs flex items-center gap-1 ${isBackgroundChecked ? 'text-green-700' : 'text-gray-400'}`}>
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Age-appropriate</span>
          </p>
        </div>

        {/* Rating (only show if verified) */}
        {isBackgroundChecked && (
          <div className="flex items-center gap-1 text-xs mb-2">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
            <span className="text-gray-500">({provider?.review_count || 180} parents)</span>
          </div>
        )}

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 20}</span>
        </p>

        {/* CTA */}
        <Button 
          className={`w-full h-8 text-sm ${isBackgroundChecked ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400'}`}
          onClick={() => isBackgroundChecked && onBook?.(service, provider)}
          disabled={!isBackgroundChecked}
          data-testid="book-kids-pottery"
        >
          {isBackgroundChecked ? 'Book kids class' : 'Requires verification'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ==================== MOBILE COMPONENTS ====================

// Mobile Sticky Pill for Creative Hobbies
export const CreativeMobilePill = ({
  startingPrice = 10,
  currency = '£',
  onTap,
}) => {
  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 lg:hidden"
      data-testid="creative-mobile-pill"
    >
      <button
        onClick={onTap}
        className="flex items-center gap-2 px-4 py-3 bg-rose-600 text-white rounded-full shadow-lg hover:bg-rose-700 transition-all active:scale-95"
      >
        <span>🎨</span>
        <span className="text-sm font-medium">Creative classes nearby</span>
        <span className="text-sm">From {currency}{startingPrice}</span>
      </button>
    </div>
  );
};

// Mobile Sticky Pill for Pottery
export const PotteryMobilePill = ({
  startingPrice = 20,
  currency = '£',
  onTap,
}) => {
  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 lg:hidden"
      data-testid="pottery-mobile-pill"
    >
      <button
        onClick={onTap}
        className="flex items-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-full shadow-lg hover:bg-orange-700 transition-all active:scale-95"
      >
        <span>🏺</span>
        <span className="text-sm font-medium">Pottery classes near you</span>
        <span className="text-sm">From {currency}{startingPrice}</span>
      </button>
    </div>
  );
};

// Mobile Bottom Sheet Content for Creative Hobbies
export const CreativeMobileSheet = ({
  services,
  providers,
  onBook,
  currency = '£',
}) => {
  const serviceTypes = [
    { type: 'art_class', icon: '🎨', name: 'Art Classes', defaultPrice: 12, label: 'classes' },
    { type: 'kids_creative', icon: '🧒', name: 'Kids Classes', defaultPrice: 10, label: 'parents' },
    { type: 'diy_assistance', icon: '🧰', name: 'DIY Help', defaultPrice: 20, label: 'projects' },
    { type: 'sewing_workshop', icon: '🧵', name: 'Sewing Workshop', defaultPrice: 18, label: 'sessions' },
    { type: 'creative_party', icon: '🎉', name: 'Craft Party', defaultPrice: 60, label: 'events' },
  ];

  return (
    <div className="p-4">
      <h3 className="font-bold text-lg mb-4">Creative Hobby Services Near You</h3>
      <div className="space-y-3">
        {serviceTypes.map((st) => {
          const service = services?.find(s => s.type === st.type);
          const provider = providers?.find(p => p.services?.some(s => s.type === st.type));
          const price = service?.price || st.defaultPrice;
          const rating = provider?.rating || 4.8;

          return (
            <div 
              key={st.type}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{st.icon}</span>
                <div>
                  <p className="font-medium text-sm">{st.name}</p>
                  <p className="text-xs text-gray-500">From {currency}{price}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-xs">
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                  <span>{rating.toFixed(1)}</span>
                </div>
                <Button 
                  size="sm" 
                  className="h-8 bg-rose-600 hover:bg-rose-700"
                  onClick={() => onBook?.(service || { type: st.type, name: st.name, price }, provider)}
                >
                  Book
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Post-Checkout Upsell Card for Creative Hobbies
export const CreativePostCheckoutCard = ({
  productType,
  prompt,
  service,
  provider,
  onAdd,
  onDismiss,
}) => {
  return (
    <Card 
      className="border-2 border-rose-300 bg-gradient-to-r from-rose-50 to-white"
      data-testid="creative-post-checkout-upsell"
    >
      <CardContent className="p-3 flex items-center gap-3">
        <span className="text-2xl">🎨</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{prompt?.title || 'Your craft kit is on the way!'}</p>
          <p className="text-xs text-gray-500">{prompt?.subtitle || 'Want help bringing it to life?'}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="text-xs h-8"
            onClick={onDismiss}
          >
            Not now
          </Button>
          <Button 
            size="sm" 
            className="text-xs h-8 bg-rose-600 hover:bg-rose-700"
            onClick={() => onAdd?.(service, provider)}
          >
            {prompt?.cta || 'Add class'} · {provider?.currency || '£'}{service?.price || 12}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Kids Safety Disclaimer for Creative Services
export const CreativeKidsSafetyDisclaimer = () => {
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-4 text-xs" data-testid="creative-kids-safety-disclaimer">
      <div className="flex items-start gap-2">
        <Shield className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-green-800 font-medium">Child Safety Notice</p>
          <p className="text-green-700 mt-1">
            All instructors for children's classes are background-checked. Parents/guardians are welcome to observe sessions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default {
  // Main Creative Hobbies
  ArtCraftClassCard,
  KidsCreativeClassCard,
  DIYAssistanceCard,
  SewingWorkshopCard,
  CreativePartyCard,
  // Pottery
  BeginnerPotteryCard,
  WheelThrowingCard,
  GlazingFiringCard,
  KidsPotteryCard,
  // Mobile
  CreativeMobilePill,
  PotteryMobilePill,
  CreativeMobileSheet,
  CreativePostCheckoutCard,
  CreativeKidsSafetyDisclaimer,
};
